import React, { useState, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SectionTitle, Spinner } from "@/components/common";
import { PyramidMark } from "@/components/Logo";
import { toast } from "sonner";
import { UploadCloud, Save, Image as ImageIcon, Loader2 } from "lucide-react";

export default function Settings() {
  const { user } = useAuth();
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const { data, refetch, isLoading } = useQuery({ queryKey: ["branding"], queryFn: async () => (await api.get("/branding")).data });
  const [sig, setSig] = useState(null);

  React.useEffect(() => { if (data) setSig({ signatory_name: data.signatory_name, signatory_title: data.signatory_title, signatory_email: data.signatory_email }); }, [data]);

  const upload = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please upload an image file (PNG/JPG/SVG)"); return; }
    setBusy(true);
    const fd = new FormData(); fd.append("file", file);
    try { await api.post("/branding/logo", fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success("Logo uploaded — it will appear on document covers, headers and footers"); refetch(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); } finally { setBusy(false); }
  };

  const saveSig = async () => {
    try { await api.put("/branding", sig); toast.success("Branding saved"); refetch(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Save failed (admin only)"); }
  };

  const { data: avOpts } = useQuery({ queryKey: ["aria-avatar-options"], queryFn: async () => (await api.get("/help/avatar-options")).data, retry: false });
  const { data: avCurrent, refetch: refetchAv } = useQuery({ queryKey: ["aria-avatar-config"], queryFn: async () => (await api.get("/help/avatar-config")).data, retry: false });
  const [av, setAv] = useState(null);
  React.useEffect(() => { if (avCurrent) setAv(avCurrent); }, [avCurrent]);
  const saveAvatar = async () => {
    try { await api.put("/help/avatar-config", av); toast.success("Aria's look & voice saved — reopen the assistant to see it"); refetchAv(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Save failed (admin only)"); }
  };

  if (isLoading || !sig) return <Spinner />;
  if (user && user.role !== "Admin") return <Navigate to="/" replace />;

  return (
    <div className="space-y-8 animate-fade-up max-w-3xl">
      <SectionTitle eyebrow="Administration" title="Branding" />

      <section className="rounded-xl border bg-card p-6 space-y-4">
        <div className="flex items-center gap-2 text-[#0f2557] dark:text-[#29abe2] font-semibold"><ImageIcon size={18} /> Company Logo</div>
        <p className="text-sm text-muted-foreground">Upload your logo lockup. It is embedded on the cover, header and footer of every generated PDF and DOCX. If none is set, the built-in Yanthra pyramid mark is used.</p>
        <div className="flex items-center gap-6 flex-wrap">
          <div className="w-48 h-28 rounded-lg border bg-white grid place-items-center overflow-hidden" data-testid="logo-preview">
            {data.logo_data_url ? <img src={data.logo_data_url} alt="logo" className="max-h-24 max-w-44 object-contain" /> : <PyramidMark size={56} />}
          </div>
          <div>
            <input ref={fileRef} type="file" accept="image/*" hidden onChange={(e) => upload(e.target.files[0])} />
            <Button onClick={() => fileRef.current?.click()} disabled={busy} className="gap-2" data-testid="upload-logo">
              {busy ? <Loader2 className="animate-spin" size={15} /> : <UploadCloud size={15} />} Upload logo
            </Button>
            <p className="text-xs text-muted-foreground mt-2">PNG, JPG or SVG · up to 3 MB</p>
          </div>
        </div>
      </section>

      <section className="rounded-xl border bg-card p-6 space-y-4">
        <div className="text-[#0f2557] dark:text-[#29abe2] font-semibold">Default Signatory</div>
        <div className="grid sm:grid-cols-3 gap-4">
          <div><Label>Name</Label><Input value={sig.signatory_name} onChange={(e) => setSig({ ...sig, signatory_name: e.target.value })} data-testid="sig-name" /></div>
          <div><Label>Title</Label><Input value={sig.signatory_title} onChange={(e) => setSig({ ...sig, signatory_title: e.target.value })} /></div>
          <div><Label>Email</Label><Input value={sig.signatory_email} onChange={(e) => setSig({ ...sig, signatory_email: e.target.value })} /></div>
        </div>
        <Button onClick={saveSig} className="gap-2" data-testid="save-branding"><Save size={15} /> Save</Button>
      </section>

      <section className="rounded-xl border bg-card p-6 space-y-4" data-testid="aria-avatar-section">
        <div className="text-[#0f2557] dark:text-[#29abe2] font-semibold">AI Assistant (Aria) · Live Avatar</div>
        <p className="text-sm text-muted-foreground">Choose the live avatar look and the voice Aria speaks with. Changes apply the next time the assistant is opened.</p>
        {av ? (
          <>
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-3 max-h-80 overflow-y-auto pr-1" data-testid="aria-avatar-grid">
              {(avOpts?.avatars || []).map((a) => (
                <button key={a.id} type="button" onClick={() => setAv({ ...av, avatar_id: a.id, avatar_preview: a.preview })}
                  data-testid="aria-avatar-option"
                  className={`relative rounded-xl overflow-hidden border-2 transition-all ${av.avatar_id === a.id ? "border-[#29abe2] ring-2 ring-[#29abe2]/30" : "border-transparent hover:border-[#29abe2]/40"}`}>
                  <img src={a.preview} alt={a.name} className="w-full h-24 object-cover" />
                  <span className="absolute bottom-0 inset-x-0 bg-black/60 text-white text-[10px] px-1.5 py-1 truncate text-left">{a.name}</span>
                </button>
              ))}
            </div>
            <div className="flex items-end gap-3 flex-wrap">
              <div className="w-48">
                <Label>Voice</Label>
                <select value={av.tts_voice} onChange={(e) => setAv({ ...av, tts_voice: e.target.value })} data-testid="aria-voice-select"
                  className="w-full h-10 rounded-md border bg-background px-2 text-sm capitalize">
                  {(avOpts?.voices || []).map((v) => <option key={v} value={v}>{v}</option>)}
                </select>
              </div>
              <Button onClick={saveAvatar} className="gap-2" data-testid="save-aria-avatar"><Save size={15} /> Save avatar &amp; voice</Button>
            </div>
          </>
        ) : <p className="text-sm text-muted-foreground">Loading avatar options…</p>}
      </section>
    </div>
  );
}
